/* global window */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import oneLine from 'common-tags/lib/oneLine';

import suitcss from '../../../helpers/suitcss';
import { list as tariffList } from '../../../propTypes/tariff';
import connectUI from '../../basics/ui/UIConnector';
import Headline from '../../basics/text/TextHeadline';
import Copy from '../../basics/text/TextCopy';
import TariffItemCompact from './TariffItemCompact';
import TariffItemPanel from './TariffItemPanel';
import TariffItemBox from './TariffItemBox';
import TariffItemComplete from './TariffItemComplete';
import matchMediaConnector from '../../../containers/service/ServiceMatchMedia';
import Callout from '../../../containers/callout/Callout';
import { capitalize, stripHTML } from '../../../helpers/str';
import LazyShowElement from '../../basics/element/LazyShowElement';
import Link from '../../basics/text/TextLink';
import {
  ENTITY_TYPE_TARIFFOPTION,
  MARKETING_LOCATION_TARIFF_LIST_BOXED,
  PAYMENT_FEE_MONTHLY,
} from '../../../helpers/constants';
import { toDecimal, toFullCurrency } from '../../../helpers/money';
import FormOptionBox from '../../basics/form/FormOptionBox';
import { selectProductSheet } from '../../../helpers/entity';
import Slider from '../../basics/slider/Slider';
import SliderSlide from '../../basics/slider/SliderSlide';

const LAYOUT_STAIRS_ASC = 'stairsAsc';
const LAYOUT_STAIRS_DESC = 'stairsDesc';

const FEATURE_SET_FULL = 'full';
const FEATURE_SET_INFO = 'info';

class TariffList extends Component {
  constructor(...args) {
    super(...args);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.state = {
      selectedTariff: null,
      activeItem: this.props.tariffs && this.props.tariffs[0].iid,
      isTariffSelected: true,
      activeOptions: [],
    };
  }

  shouldComponentUpdate() {
    const { theme, isMediaSM, isReady } = this.props;
    // prevent slider re-rendering on scroll as it causes ugly behavior on mobile touch events
    return !(theme === 'full' && isMediaSM && isReady && window.scrollY !== 0);
  }

  static getPriceText(entity, paymentType) {
    const paymentTypeStrike = `${paymentType}Strike`;
    return entity[paymentTypeStrike]
      ? oneLine`<span class="u-strikethrough">${toDecimal(entity[paymentTypeStrike])}</span> <span class="u-colorTertiary">${toFullCurrency(entity[paymentType])}</span>`
      : toFullCurrency(entity[paymentType]);
  }

  onSelectTariffHandler(index) {
    this.setState({
      selectedTariff: index,
    });
  }

  getComponentByTheme(theme) {
    switch (theme) {
      case 'panels':
        return TariffItemPanel;
      case 'full':
        return TariffItemComplete;
      case 'compact':
        return TariffItemCompact;
      case 'boxed':
        return TariffItemBox;
      default:
        return TariffItemPanel;
    }
  }

  isDarkTheme() {
    return this.props.colorScheme === 'dark';
  }

  isWithTariffHighlights() {
    const { theme, tariffs } = this.props;
    return theme === 'full' && tariffs.some(tariff => !!tariff.highlight);
  }

  isWithTariffCallout() {
    const { theme, tariffs, labelCallouts } = this.props;
    return (theme === 'full' || theme === 'boxed') && tariffs.some(tariff => labelCallouts[tariff.eid]);
  }

  isWithTariffCalloutInRow(row) {
    const { theme, tariffs, labelCallouts } = this.props;
    return theme === 'boxed' && tariffs.length > 3 && row.some(tariff => labelCallouts[tariff.eid]);
  }

  isWithListCallout() {
    const { moduleId, theme, labelCallouts } = this.props;
    return (theme === 'full' || theme === 'boxed') && !!labelCallouts[moduleId];
  }

  updateOrderProcess() {
    const { activeOptions } = this.state;
    const { addItem, removeItem, orderProcess, tariffs, tariffOptions } = this.props;
    const activeTariff = tariffs.filter(tariff => this.state.activeItem === tariff.iid)[0];
    const itemInOrderProcess = (option) => orderProcess.entities
      .filter(entity => entity.eid === option.eid).length;
    const optionsInOrderProcess = orderProcess.entities
      .filter(entity => entity.etype === ENTITY_TYPE_TARIFFOPTION);
    const optionIsIncluded = (option, array) =>
      !!array.filter(entity => entity.eid === option.eid).length;
    const optionValidWithTariff = (option) => activeTariff.options.includes(option.eid);

    activeOptions.forEach(
      option =>
        !itemInOrderProcess(option)
        && optionValidWithTariff(option)
        && addItem({ eid: option.eid, etype: ENTITY_TYPE_TARIFFOPTION }, true),
    );
    tariffOptions.forEach(
      option =>
        !optionIsIncluded(option, activeOptions)
        && optionIsIncluded(option, optionsInOrderProcess)
        && removeItem({ eid: option.eid, etype: ENTITY_TYPE_TARIFFOPTION }),
    );
  }

  handleClickBoxedItem = (event) => {
    const activeItemId = event.currentTarget.attributes['data-tariff-id'].nodeValue;
    this.setState({
      ...this.state,
      ...{
        activeItem: activeItemId,
        isTariffSelected: true,
      },
    });
  };

  handleInputChange = (event) => {
    const { activeOptions } = this.state;
    const { tariffOptions } = this.props;
    const optionId = event.currentTarget.attributes.id.nodeValue;
    const selectedOption = tariffOptions.find(option => option.eid === optionId);
    const selectedActiveOption = activeOptions.find(element => element.eid === selectedOption.eid);

    this.setState({
      ...this.state,
      activeOptions: !selectedActiveOption
        ? [...activeOptions, selectedOption]
        : [
          ...activeOptions.slice(0, activeOptions.indexOf(selectedActiveOption)),
          ...activeOptions.slice(activeOptions.indexOf(selectedActiveOption) + 1),
        ],
    });
  };

  renderTariffItem(tariff, index, withGlobalLabelCallout, withLabelCallouts, isMultiRowSM) {
    const {
      colorScheme,
      theme,
      layout,
      labelCallouts,
      moduleId,
      featureSet,
      isMediaS,
      isMediaSM,
      tariffs,
      ui,
      shoppingCartRoute,
      onSubmit,
    } = this.props;

    const activeOptionsValidWithTariff = this.state.activeOptions
      .filter(option => tariff.options.includes(option.eid));
    const additionalPaymentFees = activeOptionsValidWithTariff
      .map(option => option.paymentFee.unit)
      .reduce((a, b) => a + b, 0);
    const updatedPaymentFee = !activeOptionsValidWithTariff
      ? tariff.paymentFee.unit
      : tariff.paymentFee.unit + additionalPaymentFees;
    const updatedTariff = {
      ...tariff,
      paymentFee: { ...tariff.paymentFee, unit: updatedPaymentFee },
    };

    const isThemeFull = theme === 'full';
    const isThemeCompact = theme === 'compact';
    const isThemeBoxed = theme === 'boxed';
    const isSelected = this.state.selectedTariff === index;
    const isActiveItem =
      isThemeBoxed &&
      this.state.activeItem === tariff.iid;
    const ItemComponent = this.getComponentByTheme(theme);
    const labelCallout = !withGlobalLabelCallout
      ? labelCallouts[tariff.eid]
      : isMediaS && labelCallouts[moduleId];

    const isHighlighted = !!tariff.highlight;
    const isDarkTheme = this.isDarkTheme();
    const isInverted = (isDarkTheme && !isHighlighted && !isThemeFull)
      || (!isDarkTheme && isHighlighted);
    const isLayoutStairs = layout === LAYOUT_STAIRS_ASC || layout === LAYOUT_STAIRS_DESC;

    const isUpperRow = isMultiRowSM && index < 2;
    const isLowerRow = isMultiRowSM && index > 1;
    const equalizerGroup = `${moduleId}-calloutRow`;
    const equalizerGroupUpper = `${moduleId}1-calloutRow`;
    const equalizerGroupLower = `${moduleId}2-calloutRow`;
    const equalizerData = (!isLayoutStairs || !isThemeFull)
      ? { 'data-eq-group-l': equalizerGroup, 'data-eq-group-m': equalizerGroup }
      : {};
    const equalizerDataBoxed = (isThemeBoxed && isMultiRowSM)
      ? {
        'data-eq-group-l': equalizerGroup,
        'data-eq-group-m': isUpperRow ? equalizerGroupUpper : equalizerGroupLower,
        'data-eq-group-s': isUpperRow ? equalizerGroupUpper : equalizerGroupLower,
      }
      : { 'data-eq-group-l': equalizerGroup, 'data-eq-group-m': equalizerGroup };

    const equalizerDataSet = isThemeBoxed ? equalizerDataBoxed : equalizerData;

    const upperRow = isMultiRowSM && [tariffs[0], tariffs[1]];
    const lowerRow = isMultiRowSM && [tariffs[2], tariffs[3]];

    const upperRowHasLabelCallout = this.isWithTariffCalloutInRow(upperRow);
    const lowerRowHasLabelCallout = this.isWithTariffCalloutInRow(lowerRow);

    const linkPrimary = {
      label: ui.guiWordContinue,
      to: tariff.urlSelect,
    };

    const linkSecondary = {
      label: ui.guiWordDetails,
      to: tariff.urlDetails,
    };

    return (
      <LazyShowElement
        key={tariff.eid}
        className={suitcss({
          descendantName: 'item',
          modifiers: [
            labelCallout && 'callout',
            labelCallout && `callout${capitalize(labelCallout.colorScheme || 'primary')}`,
            isHighlighted && 'highlight',
          ],
          states: [
            isSelected && 'selected',
          ],
        }, this)}
        element="li"
      >
        {((!withGlobalLabelCallout && !isThemeBoxed) ||
            (withGlobalLabelCallout && isThemeFull)) &&
          <Callout
            theme="sticker"
            targets={[tariff.eid]}
          />
        }
        {(
          (!withGlobalLabelCallout) ||
          (isMediaS && !isThemeBoxed)
        ) && (
          <div className={suitcss({ descendantName: 'labelCallout' }, this)} >
            <div {...equalizerDataSet}>
              {labelCallout && (<Callout theme="label" callout={labelCallout} inverted={this.isDarkTheme()} />)}
            </div>
          </div>
        )}
        <ItemComponent
          isMultiRowSM={isMultiRowSM}
          colorScheme={colorScheme}
          theme={theme}
          isActive={isActiveItem}
          themeBoxedCallback={isThemeBoxed && this.handleClickBoxedItem}
          tariff={updatedTariff}
          inverted={isInverted}
          moduleId={moduleId}
          featureSet={featureSet}
          linkPrimary={isThemeFull || isThemeCompact ? linkPrimary : linkSecondary}
          linkSecondary={isThemeFull ? linkSecondary : null}
          withEqualizedHeader={!isLayoutStairs || !isThemeFull}
          isMediaS={isMediaS}
          isMediaSM={isMediaSM}
          hasLabelCallouts={withLabelCallouts && !withGlobalLabelCallout}
          isUpperRow={isUpperRow}
          isLowerRow={isLowerRow}
          upperRowHasLabelCallout={upperRowHasLabelCallout}
          lowerRowHasLabelCallout={lowerRowHasLabelCallout}
          productInformationSheetLink={selectProductSheet(tariff, 'offerList')}
          shoppingCartRoute={shoppingCartRoute}
          onSubmit={onSubmit}
        />
      </LazyShowElement>
    );
  }

  renderButtonsForSelectedTariff() {
    const { activeItem, activeOptions } = this.state;
    const { tariffs, ui, onSubmit, shoppingCartRoute, isMediaS, zweitkarte } = this.props;
    const activeTariff = tariffs.filter(tariff => activeItem === tariff.iid)[0];
    const optionsWithTariff = activeOptions.filter(
      option => activeTariff.options.includes(option.eid),
    );
    const selectedItems = [activeTariff, ...optionsWithTariff];
    return (
      <div
        className={suitcss({
          descendantName: 'button',
          utilities: ['paddingBottomS', 'row', 'itemsCenter', 'justifyCenter'],
        }, this)}
      >
        <Link
          asButton
          buttonFilled={!!zweitkarte}
          onClick={() => onSubmit(selectedItems)}
          to={shoppingCartRoute.url}
          utilities={[!isMediaS && 'marginRightS', isMediaS && 'marginTop', isMediaS && 'marginBottomS', 'sCol12']}
        >
          {zweitkarte ? ui.guiAddCart : ui.guiContinueSimOnly}
        </Link>
        {!zweitkarte &&
          <Link
            asButton
            buttonFilled
            onClick={() => this.updateOrderProcess()}
            to={activeTariff.urlSelect}
            utilities={['sCol12']}
          >
            {ui.guiContinueSimPlus}
          </Link>
        }
      </div>
    );
  }

  renderFooter() {
    return (
      <LazyShowElement className={suitcss({ descendantName: 'footer' }, this)} element="footer">
        <div className={suitcss({ descendantName: 'copy' }, this)} >
          <Copy embedded raw utilities={[this.isDarkTheme() && 'colorGray50']} >
            {this.props.tariffsCopy}
          </Copy>
        </div>
      </LazyShowElement>
    );
  }

  renderHeadline() {
    const { primaryModule } = this.props;
    return (
      <div className={suitcss({ descendantName: 'headline' }, this)} >
        <Headline
          size="l"
          element={primaryModule ? 'h1' : 'h2'}
          utilities={[
            'lowercase ',
            this.isDarkTheme() && 'colorInverted',
            'weightNormal',
          ]}
        >
          {this.props.tariffsHeadline}
        </Headline>
      </div>
    );
  }

  renderTariffOptions() {
    const { tariffOptions, theme, tariffs, isMediaS } = this.props;
    const isThemeBoxed = theme === 'boxed';
    const activeTariff = tariffs.filter(tariff => this.state.activeItem === tariff.iid)[0];
    return (
      <section
        className={suitcss({ utilities: ['flex', 'justifyCenter'] }, this)}
      >
        {
          tariffOptions
          .filter(option =>
            option.marketingLocation &&
            isThemeBoxed &&
            activeTariff.options.includes(option.eid) &&
            option.marketingLocation.includes(MARKETING_LOCATION_TARIFF_LIST_BOXED))
          .map(option =>
            (
              <FormOptionBox
                label={option.infotext && `${option.type}&nbsp;${option.name}&nbsp;${stripHTML(option.infotext).replace(
                  '{PRICE}',
                  TariffList.getPriceText(option, PAYMENT_FEE_MONTHLY),
                )}`}
                checked={!!this.state.activeOptions.find(element => element.eid === option.eid)}
                onChange={this.handleInputChange}
                id={option.eid}
                key={option.eid}
                info={option.copy && {
                  headline: `${option.type} ${option.name}`,
                  copy: option.copy,
                }}
                utilities={[!isMediaS && 'marginBottomM', 'colorInverted']}
              />
            ),
          )
        }
      </section>
    );
  }

  render() {
    const {
      tariffsHeadline,
      tariffs,
      tariffsCopy,
      theme,
      layout,
      isMediaS,
      isMediaSM,
      ui,
    } = this.props;

    const withGlobalLabelCallout = this.isWithListCallout();
    const withLabelCallouts = this.isWithTariffCallout();
    const isInverted = this.isDarkTheme();
    const isThemeBoxed = theme === 'boxed';
    const isThemeFull = theme === 'full';
    const isLayoutStairs = layout === LAYOUT_STAIRS_ASC || layout === LAYOUT_STAIRS_DESC;
    const isAdjacentLayout = !isLayoutStairs || (!isMediaS && this.isWithListCallout());
    const withHighlights = this.isWithTariffHighlights() || this.isWithTariffCallout();
    const tariffCount = tariffs.length;
    const tariffCountModifier = isThemeBoxed ? `tariffs${tariffCount}` : null;
    const isMultiRowSM = isThemeBoxed && tariffCount > 3;
    const TariffListMainElement = isMediaSM ? 'div' : 'ul';
    const sliderPaginationElement = (index) =>
      `<div>
         <div class="slider-pagination-copy">${tariffs[index].name}</div>
         <span class="slider-pagination-price">${toFullCurrency(tariffs[index].paymentFee)}</span>
         <span class="slider-pagination-price"> ${ui.guiWordMonthlyAbbr}</span>
       </div>`;


    return (
      <section
        className={suitcss(
          {
            modifiers: [
              theme,
              layout,
              isInverted && 'inverted',
              isAdjacentLayout && 'adjacent',
              withHighlights && 'highlights',
            ],
            utilities: [withLabelCallouts && !withGlobalLabelCallout && 'marginTopXXL'],
          },
          this,
        )}
        data-count={tariffs.length}
      >
        {tariffsHeadline && (
          <header className={suitcss({ descendantName: 'header' }, this)}>
            {this.renderHeadline()}
          </header>
        )}
        {(!isMediaS || isThemeFull || isThemeBoxed) && (
          <Callout theme="label" targets={[this.props.moduleId]} inverted={isInverted} />
        )}
        <TariffListMainElement
          className={suitcss({ descendantName: 'main', modifiers: [tariffCountModifier] }, this)}
        >
          {isThemeFull && isMediaSM && tariffs.length >= 3 ? (
            <div
              className={suitcss(
                {
                  descendantName: 'slider',
                  utilities: ['hidden', 'smBlock'],
                },
                this,
              )}
            >
              <Slider
                theme={isInverted && 'dark'}
                config={{
                  centeredSlides: true,
                  slidesPerView: isMediaS ? 1.1 : 'auto',
                  spaceBetween: 8,
                  pagination: {
                    alignTop: true,
                    renderBullet: (index, className) =>
                      `<span class="${className}">` +
                      `${sliderPaginationElement(index)}` +
                      '</span>',
                  },
                }}
              >
                {tariffs.map((tariff, index) => (
                  <SliderSlide key={index}>
                    {this.renderTariffItem(tariff, [
                      tariff.length > 1 && 'hidden',
                      tariff.length > 1 && 'sBlock',
                    ])}
                  </SliderSlide>
                ))}
              </Slider>
            </div>
          ) : (
            tariffs.map((tariff, index) =>
              this.renderTariffItem(
                tariff,
                index,
                withGlobalLabelCallout,
                withLabelCallouts,
                isMultiRowSM,
              ),
            )
          )}
        </TariffListMainElement>
        {isThemeBoxed && this.renderTariffOptions()}
        {tariffsCopy && this.renderFooter()}
        {
          isThemeBoxed &&
          this.state.isTariffSelected &&
          this.renderButtonsForSelectedTariff()
        }
        <Callout
          theme="badge"
          targets={[this.props.moduleId]}
          inverted={isInverted}
        />
      </section>
    );
  }
}

TariffList.propTypes = {
  tariffsHeadline: PropTypes.string,
  tariffsCopy: PropTypes.string,
  colorScheme: PropTypes.oneOf(['dark', 'light']),
  tariffs: tariffList.isRequired,
  labelCallouts: PropTypes.object,
  primaryModule: PropTypes.bool,
  theme: PropTypes.oneOf(['full', 'compact', 'panels', 'boxed']),
  featureSet: PropTypes.oneOf([FEATURE_SET_FULL, FEATURE_SET_INFO]),
  layout: PropTypes.oneOf([LAYOUT_STAIRS_ASC, LAYOUT_STAIRS_DESC]),
  moduleId: PropTypes.string.isRequired,
  isMediaS: PropTypes.bool,
  isMediaSM: PropTypes.bool,
  ui: PropTypes.shape({
    guiWordDetails: PropTypes.string.isRequired,
    guiWordContinue: PropTypes.string.isRequired,
  }),
  onSubmit: PropTypes.func.isRequired,
  shoppingCartRoute: PropTypes.object,
  tariffOptions: PropTypes.array,
  addItem: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
  orderProcess: PropTypes.object.isRequired,
  zweitkarte: PropTypes.bool,
  isReady: PropTypes.bool,
};


TariffList.defaultProps = {
  theme: 'full',
  featureSet: 'full',
  labelCallouts: {},
};

export default compose(
  matchMediaConnector(['isMediaS', 'isMediaSM']),
  connectUI(),
)(TariffList);
